import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import masshappyIcon from "@/assets/masshappy-icon.png";

const APP_URL = "https://app.masshappy.com";

const CARD_COUNT = 12;

type TarotCard = {
  arcana: string;
  name: string;
  archetype: string;
  keys: string[];
  reading: string;
};

const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
};

const ROMAN: [number, string][] = [
  [10, "X"],
  [9, "IX"],
  [5, "V"],
  [4, "IV"],
  [1, "I"],
];

const toRoman = (arcana: string) => {
  const match = arcana.match(/\d+/);
  if (!match) return arcana.toUpperCase();
  let n = Number(match[0]);
  if (n === 0) return "0";
  let out = "";
  for (const [value, symbol] of ROMAN) {
    while (n >= value) {
      out += symbol;
      n -= value;
    }
  }
  return out;
};

const Frame = ({ tone }: { tone: "light" | "dark" }) => {
  const line = tone === "light" ? "border-primary-foreground/35" : "border-primary/30";
  const corner = tone === "light" ? "border-primary-foreground/50" : "border-primary/40";
  return (
    <>
      <div className={`pointer-events-none absolute inset-[10px] rounded-[12px] border ${line}`} />
      {[
        "left-[16px] top-[16px]",
        "right-[16px] top-[16px]",
        "left-[16px] bottom-[16px]",
        "right-[16px] bottom-[16px]",
      ].map((pos) => (
        <span
          key={pos}
          className={`pointer-events-none absolute h-[9px] w-[9px] border ${corner} ${pos}`}
        />
      ))}
    </>
  );
};

const svgProps = {
  width: 76,
  height: 76,
  viewBox: "0 0 64 64",
  fill: "none",
  stroke: "hsl(var(--primary))",
  strokeWidth: 1.1,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

const GLYPHS: Array<() => JSX.Element> = [
  () => (
    <svg {...svgProps}>
      <circle cx="32" cy="20" r="7" />
      <path d="M32 27v18M22 34h20M26 56l6-11 6 11" />
    </svg>
  ),
  () => (
    <svg {...svgProps}>
      <path d="M14 40c6-18 30-18 36 0" />
      <circle cx="32" cy="24" r="5" />
      <path d="M16 48h32" />
    </svg>
  ),
  () => (
    <svg {...svgProps}>
      <circle cx="32" cy="26" r="10" />
      <path d="M32 6v4M32 42v4M12 26h4M48 26h4M18 12l3 3M46 12l-3 3" />
      <path d="M18 50h28" />
    </svg>
  ),
  () => (
    <svg {...svgProps}>
      <path d="M40 12a16 16 0 100 32 20 20 0 010-32z" />
      <path d="M20 24l2 4 4 2-4 2-2 4-2-4-4-2 4-2z" />
      <path d="M16 52h32" />
    </svg>
  ),
  () => (
    <svg {...svgProps}>
      <path d="M32 10v34" />
      <path d="M18 22h28" />
      <circle cx="18" cy="34" r="6" />
      <circle cx="46" cy="34" r="6" />
      <path d="M22 52h20" />
    </svg>
  ),
  () => (
    <svg {...svgProps}>
      <path d="M14 44c6-6 12-6 18 0s12 6 18 0" />
      <path d="M14 34c6-6 12-6 18 0s12 6 18 0" />
      <circle cx="32" cy="16" r="6" />
    </svg>
  ),
  () => (
    <svg {...svgProps}>
      <path d="M32 8l6 14 14 2-10 10 3 15-13-8-13 8 3-15-10-10 14-2z" />
    </svg>
  ),
  () => (
    <svg {...svgProps}>
      <circle cx="32" cy="32" r="16" />
      <path d="M32 16v32M16 32h32" />
    </svg>
  ),
  () => (
    <svg {...svgProps}>
      <path d="M32 52V26" />
      <path d="M32 26c-8 0-14-6-14-14 8 0 14 6 14 14z" />
      <path d="M32 32c8 0 14-6 14-14-8 0-14 6-14 14z" />
      <path d="M22 54h20" />
    </svg>
  ),
  () => (
    <svg {...svgProps}>
      <rect x="18" y="14" width="28" height="36" rx="4" />
      <path d="M26 24h12M26 32h12M26 40h8" />
    </svg>
  ),
  () => (
    <svg {...svgProps}>
      <path d="M16 48L48 16" />
      <circle cx="20" cy="44" r="6" />
      <circle cx="44" cy="20" r="6" />
    </svg>
  ),
  () => (
    <svg {...svgProps}>
      <circle cx="32" cy="32" r="14" />
      <path d="M32 18a14 14 0 000 28 7 7 0 000-28z" fill="hsl(var(--primary) / 0.15)" />
    </svg>
  ),
];


const CardFlipDemo = () => {
  const { t, i18n } = useTranslation();
  const reducedMotion = usePrefersReducedMotion();
  const [index, setIndex] = useState(() => Math.floor(Math.random() * CARD_COUNT));
  const [flipped, setFlipped] = useState(false);
  const timer = useRef<number>();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const card = useMemo<TarotCard>(() => {
    const raw = t(`cardDemo.cards.${index}`, { returnObjects: true }) as TarotCard;
    return {
      arcana: raw?.arcana ?? "",
      name: raw?.name ?? "",
      archetype: raw?.archetype ?? "",
      keys: Array.isArray(raw?.keys) ? raw.keys : [],
      reading: raw?.reading ?? "",
    };
  }, [t, index, i18n.language]);

  const flip = useCallback(() => setFlipped(true), []);

  const another = useCallback(() => {
    setFlipped(false);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(
      () => {
        setIndex((prev) => {
          let next = prev;
          while (next === prev) {
            next = Math.floor(Math.random() * CARD_COUNT);
          }
          return next;
        });
        setFlipped(true);
      },
      reducedMotion ? 0 : 400
    );
  }, [reducedMotion]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      flip();
    }
  };

  const duration = reducedMotion ? "0ms" : "700ms";

  const roman = useMemo(() => toRoman(card.arcana), [card.arcana]);
  const Glyph = GLYPHS[index % GLYPHS.length];

  return (
    <div className="flex flex-col items-center text-center">
      <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
        {t("cardDemo.hint")}
      </p>

      <div
        className="mt-5 w-[240px] sm:w-[270px] md:w-[300px]"
        style={{ perspective: "1400px", WebkitPerspective: "1400px" } as React.CSSProperties}
      >
        <div
          role="button"
          tabIndex={0}
          aria-label={t("cardDemo.flipAria")}
          aria-pressed={flipped}
          onClick={flip}
          onKeyDown={onKeyDown}
          className="relative w-full cursor-pointer rounded-[18px] outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background"
          style={
            {
              aspectRatio: "260 / 400",
              transformStyle: "preserve-3d",
              WebkitTransformStyle: "preserve-3d",
              transition: `transform ${duration} cubic-bezier(0.22, 1, 0.36, 1)`,
              WebkitTransition: `-webkit-transform ${duration} cubic-bezier(0.22, 1, 0.36, 1)`,
              transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
              WebkitTransform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
            } as React.CSSProperties
          }
        >
          {/* Dorso */}
          <div
            className="absolute inset-0 overflow-hidden rounded-[20px] border border-primary-dark/40 shadow-medium"
            style={
              {
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                background:
                  "radial-gradient(75% 55% at 50% 50%, hsl(var(--primary) / 0.98), hsl(var(--primary-dark)) 85%)",
              } as React.CSSProperties
            }
          >
            <div
              className="absolute inset-0 opacity-35"
              style={{
                backgroundImage:
                  "radial-gradient(hsl(var(--primary-foreground) / 0.45) 1px, transparent 1.2px)",
                backgroundSize: "13px 13px",
              }}
            />
            {/* Marco doble con esquinas */}
            <div className="pointer-events-none absolute inset-[10px] rounded-[13px] border border-wellness-gold/50" />
            <div className="pointer-events-none absolute inset-[14px] rounded-[10px] border border-wellness-gold/25" />
            {[
              "left-[18px] top-[18px]",
              "right-[18px] top-[18px]",
              "left-[18px] bottom-[18px]",
              "right-[18px] bottom-[18px]",
            ].map((pos) => (
              <span
                key={pos}
                className={`pointer-events-none absolute h-[10px] w-[10px] border border-wellness-gold/70 ${pos}`}
              />
            ))}

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative flex h-[152px] w-[152px] items-center justify-center">
                <span
                  className="absolute inset-[-14px] rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle, hsl(var(--primary-light) / 0.45), transparent 70%)",
                  }}
                />
                <span className="absolute inset-0 rounded-full border border-wellness-gold/45" />
                <span className="absolute inset-[10px] rounded-full border border-primary-foreground/30" />
                <img
                  src={masshappyIcon}
                  alt=""
                  aria-hidden="true"
                  width={92}
                  height={106}
                  className="relative h-[92px] w-auto brightness-0 invert opacity-95"
                />
              </div>
            </div>
          </div>


          {/* Cara */}
          <div
            className="absolute inset-0 overflow-hidden rounded-[18px] border border-border/60 bg-card shadow-medium"
            style={
              {
                backfaceVisibility: "hidden",
                WebkitBackfaceVisibility: "hidden",
                transform: "rotateY(180deg)",
                WebkitTransform: "rotateY(180deg)",
              } as React.CSSProperties
            }
          >
            <Frame tone="dark" />
            <div className="relative flex h-full flex-col items-center px-7 pb-7 pt-7">
              <span className="text-[11px] uppercase tracking-[0.4em] text-primary/70">
                {roman}
              </span>

              <div
                className="mt-4 flex w-full flex-1 items-center justify-center rounded-t-[999px] border border-primary/15"
                style={{
                  background:
                    "linear-gradient(180deg, hsl(var(--wellness-gold) / 0.10), transparent 70%)",
                }}
              >
                <Glyph />
              </div>

              <h3 className="mt-5 font-serif text-[28px] leading-none text-foreground">
                {card.name}
              </h3>
              <p className="mt-2 font-serif text-sm italic text-muted-foreground">
                {card.archetype}
              </p>
              <span className="my-4 block h-px w-10 bg-primary/40" />
              <p className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">
                {card.keys.join(" · ")}
              </p>
            </div>
          </div>
        </div>
      </div>


      {flipped && (
        <div className="mt-8 max-w-md animate-fade-in">
          <p className="font-serif text-base md:text-lg leading-relaxed text-foreground">
            {card.reading}
          </p>
          <p className="mt-5 text-xs text-muted-foreground leading-relaxed">
            {t("cardDemo.sampleNote")}
          </p>
          <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
            {t("cardDemo.personalNote")}
          </p>
          <div className="mt-6">
            <Button asChild className="shadow-medium">
              <a href={APP_URL} target="_blank" rel="noopener noreferrer">
                {t("cardDemo.cta")}
              </a>
            </Button>
          </div>
          <button
            type="button"
            onClick={another}
            className="mt-5 text-xs text-muted-foreground underline underline-offset-4 hover:text-primary transition-colors"
          >
            {t("cardDemo.another")}
          </button>
        </div>
      )}
    </div>
  );
};

export default CardFlipDemo;
